import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import createFootprintAirFreightApi from 'api/airFreightFootprint';
import { showToast } from 'modules/app/toasterSlice';
import { appContextSelector } from 'modules/app/appSlice';
import { AppThunk, RootState } from 'store';
import axios, { CancelTokenSource } from 'axios';
import { fixFloat } from 'utils/numberFormatter';
import { currentUserSelector, customMetadataSelector } from 'modules/user/userSlice';
import checkJob from 'utils/checkJob';

const CancelToken = axios.CancelToken;
let source: CancelTokenSource = CancelToken.source();

interface FootprintInfo {
  emissionName?: string;
  description?: string;
  emissionScope?: Option;
  [key: string]: string | Option | Date | undefined | boolean | number;
}

export interface CustomMetadata {
  [key: string]: string | Option | number | undefined | boolean | string[] | Date;
}

export interface AirFreightFootprintState extends CustomMetadata {
  amount?: number;
  totalKilosCo2?: number;
  isLoading: boolean;
  emissionName?: string;
  country?: Option;
  department?: Option;
  description?: string;
  emissionScope?: Option;
  isFootprintInfoInvalid: boolean;
  selectedCustomMetadata?: string[];
  fileUploading?: boolean;
  fileErrors?: string[];
  fileSuccess?: boolean;
  footprintsCount?: number;
  fileProcessing?: boolean;
  progressPercentage?: number;
  isUploadingFileCancelled?: boolean;
  storageBlobName?: string;
}

//endregion

const initialState: AirFreightFootprintState = {
  mode: 'upload',
  isLoading: false,
  isFootprintInfoInvalid: true,
};

const tonnesFactor = 1000;

const slice = createSlice({
  name: 'airFreightFootprint',
  initialState,
  reducers: {
    reset: (): AirFreightFootprintState => {
      return initialState;
    },
    resetAmount: (state: AirFreightFootprintState): AirFreightFootprintState => {
      return { ...state, amount: 0, totalKilosCo2: 0 };
    },
    setLoading: (state: AirFreightFootprintState, action: PayloadAction<boolean>): AirFreightFootprintState => {
      state.isLoading = action.payload;
      return state;
    },
    setFootprintInfo: (state: AirFreightFootprintState, action: PayloadAction<FootprintInfo>): AirFreightFootprintState => {
      Object.keys(action.payload).map(key => {
        state[key] = action.payload[key];
      });
      return state;
    },
    setFootprintInfoInvalid: (state: AirFreightFootprintState, action: PayloadAction<boolean>): AirFreightFootprintState => {
      state.isFootprintInfoInvalid = action.payload;
      return state;
    },
    setSelectedCustomMetadata: (state: AirFreightFootprintState, action: PayloadAction<string[]>): AirFreightFootprintState => {
      state.selectedCustomMetadata = action.payload;
      return state;
    },
    resetUploadState: (state: AirFreightFootprintState, action: PayloadAction<boolean>): AirFreightFootprintState => {
      if (!action.payload) {
        source.cancel();
      }
      state.fileUploading = action.payload;
      state.fileProcessing = action.payload;
      state.progressPercentage = 0;
      state.isUploadingFileCancelled = true;
      state.fileErrors = [];
      state.fileSuccess = false;
      state.amount = undefined;
      state.totalKilosCo2 = undefined;
      state.footprintsCount = undefined;
      return state;
    },
    setFileUploading: (state: AirFreightFootprintState, action: PayloadAction<boolean>): AirFreightFootprintState => {
      state.fileUploading = action.payload;
      return state;
    },
    setFootprintsCount: (state: AirFreightFootprintState, action: PayloadAction<number>): AirFreightFootprintState => {
      state.footprintsCount = action.payload;
      return state;
    },
    setFileErrors: (state: AirFreightFootprintState, action: PayloadAction<string[]>): AirFreightFootprintState => {
      state.fileErrors = action.payload;
      return state;
    },
    setFileSuccess: (state: AirFreightFootprintState, action: PayloadAction<boolean>): AirFreightFootprintState => {
      state.fileSuccess = action.payload;
      return state;
    },
    setUploadTotalAmount: (
      state: AirFreightFootprintState,
      action: PayloadAction<{ amount: number; kilosCo2: number }>,
    ): AirFreightFootprintState => {
      state.amount = action.payload.amount;
      state.totalKilosCo2 = action.payload.kilosCo2;
      return state;
    },
    setProgressPercentage: (state: AirFreightFootprintState, action: PayloadAction<number>): AirFreightFootprintState => {
      state.progressPercentage = action.payload;
      return state;
    },
    setFileProcessing: (state: AirFreightFootprintState, action: PayloadAction<boolean>): AirFreightFootprintState => {
      state.fileProcessing = action.payload;
      return state;
    },
    resetUploadingFileCancelled: (state: AirFreightFootprintState): AirFreightFootprintState => {
      state.isUploadingFileCancelled = false;
      return state;
    },
    setStorageBlobName: (state: AirFreightFootprintState, action: PayloadAction<string>): AirFreightFootprintState => {
      state.storageBlobName = action.payload;
      return state;
    },
  },
});

const {
  reset,
  setFootprintInfo,
  setLoading,
  resetAmount,
  setFootprintInfoInvalid,
  setSelectedCustomMetadata,
  resetUploadState,
  setFileUploading,
  setFileErrors,
  setFileSuccess,
  setUploadTotalAmount,
  setFootprintsCount,
  setFileProcessing,
  setProgressPercentage,
  resetUploadingFileCancelled,
  setStorageBlobName,
} = slice.actions;

export { reset, setFootprintInfo, resetAmount, setFootprintInfoInvalid, setSelectedCustomMetadata, resetUploadState };
export default slice.reducer;

export const createAirFreightFootprint = (errorCb?: CallableFunction, successCb?: CallableFunction): AppThunk => async (
  dispatch,
  getState,
): Promise<void> => {
  const context = appContextSelector(getState());
  const airFreightAPI = createFootprintAirFreightApi(context);
  const app = getState().app;
  const currentUser = currentUserSelector(getState());
  const customMetadata = customMetadataSelector(getState()) ?? [];
  const { storageBlobName, emissionName, description, ...restProps } = getState().airFreightFootprint;
  const isConnectContext = context === 'connect';
  const partnershipId = isConnectContext ? currentUser?.partnershipId ?? '' : app.partnershipId ?? '';
  const customerId = currentUser?.customerId ?? '';

  const customMetadataPayload: CustomMetadata = {};
  customMetadata?.map(
    ({ internalName }) =>
      //@ts-ignore
      (customMetadataPayload[internalName] = restProps[internalName]?.value ? restProps[internalName]?.value : restProps[internalName]),
  );
  dispatch(setLoading(true));
  try {
    source = CancelToken.source();
    if (storageBlobName && emissionName && description) {
      const payload = {
        storageBlobName,
        emissionName,
        description,
        ...customMetadataPayload,
      };
      const response = isConnectContext
        ? await airFreightAPI.createBatchOrderForPartner(payload, source.token, partnershipId)
        : await airFreightAPI.createBatchOrderForCustomer(payload, source.token, customerId);

      if (response.jobId) {
        const result = await checkJob<FlightsProcessingFileSummary>({
          partnershipId: response.partnershipId,
          jobId: response.jobId,
          context,
          module: 'airFreight',
          setFileProcessingCb: (state: boolean) => dispatch(setFileProcessing(state)),
          setFileUploadingCb: (state: boolean) => dispatch(setFileUploading(state)),
          setProgressPercentageCb: (percentage: number) => dispatch(setProgressPercentage(percentage)),
        });
        const errorMessages = result?.messages?.filter(({ type }) => type.toLowerCase() !== 'information')?.map(m => m.message);
        if (result.status === 'Succeeded' && errorMessages.length === 0) {
          successCb && successCb();
        } else {
          dispatch(showToast({ variant: 'error', titlei18nKey: 'offset:errorTitle', descriptioni18nKey: 'footprints:createFootprintError' }));
          if (Array.isArray(errorMessages)) {
            dispatch(setFileErrors(errorMessages));
          }
          errorCb && errorCb();
        }
      }
    }

    dispatch(setLoading(false));
  } catch (err) {
    dispatch(setLoading(false));
    dispatch(showToast({ variant: 'error', titlei18nKey: 'offset:errorTitle', descriptioni18nKey: 'footprints:createFootprintError' }));
    errorCb && errorCb();
  }
};

export const processFile = (file: File): AppThunk => async (dispatch, getState): Promise<void> => {
  dispatch(setFileSuccess(false));
  dispatch(setFileProcessing(true));
  dispatch(setUploadTotalAmount({ amount: 0, kilosCo2: 0 }));
  dispatch(resetUploadingFileCancelled());
  const app = getState().app;
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const isConnectContext = context === 'connect';
  const airFreightAPI = createFootprintAirFreightApi(context);
  const partnershipId = isConnectContext ? currentUser?.partnershipId ?? '' : app.partnershipId ?? '';
  const customerId = currentUser?.customerId ?? '';

  try {
    source = CancelToken.source();

    const batchResponse = isConnectContext
      ? await airFreightAPI.uploadFootprintBatchForPartner(file, source.token, partnershipId)
      : await airFreightAPI.uploadFootprintBatchForCustomer(file, source.token, customerId);

    if (batchResponse.partnershipId && batchResponse.jobId) {
      const response = await checkJob<FlightsFootprintProcessingFileSummary>({
        partnershipId: batchResponse.partnershipId,
        jobId: batchResponse.jobId,
        context,
        module: 'airFreight',
        setFileProcessingCb: (state: boolean) => dispatch(setFileProcessing(state)),
        setFileUploadingCb: (state: boolean) => dispatch(setFileUploading(state)),
        setProgressPercentageCb: (percentage: number) => dispatch(setProgressPercentage(percentage)),
      });

      const summary = response?.summary?.footprints;
      const footprintsCount = summary.count;
      const storageBlobName = summary.storageBlobName;

      if (storageBlobName) {
        footprintsCount && dispatch(setFootprintsCount(footprintsCount));
        dispatch(setStorageBlobName(storageBlobName));
        dispatch(setFileErrors([]));
        dispatch(setFileSuccess(true));
        const kilosCo2 = response.summary.footprints?.totalCo2Kilos;
        dispatch(setUploadTotalAmount({ amount: kilosCo2 / tonnesFactor, kilosCo2 }));
      } else {
        if (Array.isArray(response.messages)) {
          const textMessages = response.messages.map(({ message }) => message);
          dispatch(setFileErrors(textMessages));
        } else setFileErrors([]);
      }
    }
  } catch (err) {
    // @ts-ignore
    if (err && err.data) {
      // @ts-ignore
      const { data } = err.data;
      if (data && Array.isArray(data)) {
        dispatch(setFileErrors(data));
        // @ts-ignore
      }
    }
    dispatch(setFileUploading(false));
  }
};

export const flightAmountSelector = createSelector(
  (state: RootState) => state.airFreightFootprint,
  state => {
    const { amount } = state;

    return fixFloat(amount ?? 0);
  },
);
